import React, { useMemo } from "react"
import { Row, Col, Typography, Divider, Collapse } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { numberToCurrency } from "helpers/string"
import { colors } from "themes/variables"

const { Text } = Typography

const PreviousPriceDetail = ({ booking }) => {
  const { payment_summary = {}, payment_history = [], booking_add_ons = [] } = booking || {}
  const {
    total_price,
    tax_and_fee,
    adult,
    child,
    infant,
    remaining_balance,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate,
    custom_fees,
    discount_amount,
    discount_code
  } = payment_summary ?? {}
  const { t } = useTranslation()
  const summaryDetails = useMemo(() => {
    const details = []

    const addPricingDetails = (_type, count, price, label) => {
      if (count && price) {
        details.push({ label: `${label} x ${count}`, value: price })
      }
    }

    const addPerPersonPricing = () => {
      if (pricing_type !== "charge_per_person") return
      addPricingDetails(adult, adult?.count, adult?.price, "Adult")
      addPricingDetails(child, child?.count, child?.price, "Child")
      addPricingDetails(infant, infant?.count, infant?.price, "Infant")
      addPricingDetails(passenger, passenger?.count, passenger?.price, "Passenger")
    }

    const addFlatRatePricing = () => {
      if (pricing_type !== "charge_flat_rate") return
      details.push({ label: "Flat Rate For Main Guests", value: flat_rate ?? 0 })
      addPricingDetails(extra_adult, extra_adult?.count, extra_adult?.price, "Extra Adult")
      addPricingDetails(extra_child, extra_child?.count, extra_child?.price, "Extra Child")
      addPricingDetails(extra_infant, extra_infant?.count, extra_infant?.price, "Extra Infant")
      addPricingDetails(extra_passenger, extra_passenger?.count, extra_passenger?.price, "Extra Passenger")
    }

    addPerPersonPricing()
    addFlatRatePricing()

    if (discount_amount > 0) {
      const discountLabel = discount_code ? `Discount (${discount_code})` : "Discount"
      details.push({ label: discountLabel, value: -discount_amount, isDiscount: true })
    }

    const addOnsTotal = (booking_add_ons ?? []).reduce((sum, item) => sum + Number(item.total_price || 0), 0)
    if (addOnsTotal > 0) {
      details.push({
        label: t("booking.add_ons.title", { defaultValue: "Add-ons" }),
        value: addOnsTotal
      })
    }

    // Build Tax & Fees as a grouped entry with children
    const customFeeItems = (custom_fees ?? []).map((fee) => ({
      label: fee.title,
      amount: numberToCurrency(Number(fee.price))
    }))
    const platformFeeAmount = tax_and_fee ?? 0
    if (platformFeeAmount > 0) {
      customFeeItems.push({ label: "Platform Fee", amount: numberToCurrency(platformFeeAmount) })
    }
    const customFeeTotal = (custom_fees ?? []).reduce((sum, fee) => sum + Number(fee.price), 0)
    const taxFeesTotal = customFeeTotal + platformFeeAmount
    if (taxFeesTotal > 0) {
      details.push({
        label: "Tax & Fees",
        value: taxFeesTotal,
        children: customFeeItems.length > 0 ? customFeeItems : undefined
      })
    }

    return details
  }, [
    adult, child, infant, passenger,
    tax_and_fee, flat_rate, extra_adult,
    extra_child, extra_infant, extra_passenger,
    pricing_type, custom_fees, discount_amount, discount_code,
    booking_add_ons, t
  ])

  return (
    <div>
      {summaryDetails
        .filter(({ value }) => value !== undefined && value !== null && value !== "")
        .map(({ label, value, children, isDiscount }) =>
          children?.length > 0 ? (
            <Collapse
              key={`${label}-${value}`}
              ghost
              style={{ marginTop: 8 }}
              items={[
                {
                  key: "tax-fees",
                  styles: {
                    header: { padding: 0 },
                    body: { padding: "4px 0 0 16px" }
                  },
                  label: (
                    <Row justify="space-between">
                      <Col><Text>{label}</Text></Col>
                      <Col><Text>&nbsp;&nbsp;{numberToCurrency(value)}</Text></Col>
                    </Row>
                  ),
                  children: children.map((child) => (
                    <Row key={`${child.label}-${child.amount}`} justify="space-between" style={{ marginTop: 4 }}>
                      <Col><Text type="secondary">{child.label}</Text></Col>
                      <Col><Text type="secondary">&nbsp;&nbsp;{child.amount}</Text></Col>
                    </Row>
                  ))
                }
              ]}
            />
          ) : (
            <Row key={`${label}-${value}`} justify="space-between" style={{ marginTop: 8 }}>
              <Col><Text type={isDiscount ? "danger" : undefined}>{label}</Text></Col>
              <Col><Text fw={500} type={isDiscount ? "danger" : undefined}>&nbsp;&nbsp;{numberToCurrency(value)}</Text></Col>
            </Row>
          )
        )}
      <Divider style={{ margin: "16px 0" }} />

      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.total")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(total_price)}</Text>
        </Col>
      </Row>
      {payment_history?.map((history) => {
        const { type, method, last4, amount, id } = history || {}
        const isRefund = type?.toLowerCase() === "refund"
        const key = id || `${type}-${method}-${last4}-${amount}`
        return (
          <Row key={key} justify="space-between" style={{ marginTop: 8 }}>
            <Col>
              <Text>
                {type || ""} ({method || ""}
                {last4 && ` **** ${last4}`})
              </Text>
            </Col>
            <Col>
              <Text color={isRefund ? colors.error : colors.textPrimary}>{amount ? numberToCurrency(amount) : ""}</Text>
            </Col>
          </Row>
        )
      })}
      <Row justify="space-between" style={{ marginTop: 8 }}>
        <Col>
          <Text strong>{t("booking.labels.remaining_balance")}</Text>
        </Col>
        <Col>
          <Text strong>{numberToCurrency(remaining_balance)}</Text>
        </Col>
      </Row>
    </div>
  )
}

PreviousPriceDetail.propTypes = {
  booking: PropTypes.object.isRequired,
}

export default PreviousPriceDetail
